import { Box, Button, ComponentWithAs, Flex, Icon, IconButton, IconProps, Select, Text } from '@chakra-ui/react';
import { PaginationState } from '@tanstack/react-table';
import { ChangeEvent, useState } from 'react';

import TextInput from '@/components/common/Forms/inputs/TextInput/TextInput';
import { ArrowRightCircleIcon } from '@/components/common/Icons/ArrowRightCircleIcon';
import { ChevronLeftFirstIcon } from '@/components/common/Icons/ChevronLeftFirstIcon';
import { ChevronLeftIcon } from '@/components/common/Icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/components/common/Icons/ChevronRightIcon';
import { ChevronRightLastIcon } from '@/components/common/Icons/ChevronRightLastIcon';
import { PAGE_SIZE_OPTIONS } from '@/constants/common';
import { useNextTranslation } from '@/i18n/client';

interface Props {
  pagination: {
    pageCount: number; // total number of pages
    itemCount: number; // total number of items
    state: PaginationState;
  };
  onPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  isAutoHide?: boolean;
  enabledJumper?: boolean;
  enabledPageSizeController?: boolean;
}

const CONTROL_KEYS = [
  'Enter',
  'Delete',
  'Backspace',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Tab',
  'Alt',
  'Meta',
  'Shift',
  'Control',
  'CapsLock',
];

const TablePagination = ({
  pagination,
  onPaginationChange,
  isAutoHide = true,
  enabledJumper = true,
  enabledPageSizeController = true,
}: Props) => {
  const { t } = useNextTranslation();

  const {
    itemCount,
    pageCount,
    state: { pageIndex, pageSize },
  } = pagination;

  const NO_PAGE = 0;
  const currentPage = pageIndex + 1;
  const [inputPage, setInputPage] = useState<number>(NO_PAGE);

  const inputLength = Math.max(String(pageCount).length, 2);
  const rowNumStart = (pageSize * pageIndex + 1).toLocaleString();
  const rowNumEnd = Math.min(pageSize * pageIndex + pageSize, itemCount).toLocaleString();
  const isFirstPage = pageIndex <= 0;
  const isLastPage = pageIndex >= pageCount - 1;

  const gotoPage = (page: number) => {
    if (page === NO_PAGE) {
      return;
    } else if (page > pageCount) {
      onPaginationChange({ pageIndex: pageCount - 1, pageSize });
    } else if (page < 1) {
      onPaginationChange({ pageIndex: 0, pageSize });
    } else {
      onPaginationChange({ pageIndex: page - 1, pageSize });
    }
    setInputPage(NO_PAGE);
  };

  const PageNumButton = (pNumber: number, active = false) => (
    <Button
      bgColor={active ? 'black' : 'unset'}
      color={active ? 'white' : 'black'}
      onClick={() => gotoPage(pNumber)}
      key={pNumber}
      paddingX={3}
      paddingY={0}
      size='sm'
      fontWeight='700'
      _hover={active ? { backgroundColor: 'black', color: 'white' } : { backgroundColor: 'gray.100', color: 'black' }}
      data-testid={`page-${pNumber}`}
    >
      {pNumber}
    </Button>
  );

  const renderPages = (startIndex: number, count = 1) => {
    return Array.from({ length: pageCount }, (_, i) => i + 1)
      .slice(startIndex, startIndex + count)
      .map((pNum) => PageNumButton(pNum, pageIndex === pNum - 1));
  };

  const renderFrontPages = (start: number, end: number) => (
    <>
      {renderPages(start, 5)}
      {DotsButton()}
      {renderPages(end)}
    </>
  );

  const renderRearPages = (start: number, end: number) => (
    <>
      {renderPages(start, 1)}
      {DotsButton()}
      {renderPages(end - 4, 5)}
    </>
  );

  const renderPageTemplate = () => {
    // Show page buttons based on the current page index.
    // But only show up to 10 pages at a time. (e.g. 1-10, 11-20, 21-30, ...)
    const base = Math.floor(pageIndex / 10);
    const remainder = pageIndex % 10;

    const start = base * 10;
    const end = Math.min(start + 9, pageCount - 1);
    const total = end - start + 1;

    if (total <= 6) {
      return renderPages(start, total);
    } else if (remainder < 5) {
      return renderFrontPages(start, end);
    } else {
      return renderRearPages(start, end);
    }
  };

  const handlePageInput = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/\D/g, '');
    const pageNum = Number(inputValue);

    if (!inputValue || !pageNum) {
      setInputPage(NO_PAGE);
    } else if (pageNum < 1) {
      setInputPage(1);
    } else if (pageNum > pageCount) {
      setInputPage(pageCount);
    } else {
      setInputPage(pageNum);
    }
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    // When page size changed, the list should set back to the first page.
    onPaginationChange({ pageIndex: 0, pageSize: Number(event.target.value) });
  };

  if (isAutoHide && itemCount <= 10) {
    // When the number of rows in the table is less than 10 or empty, Pagination should be hidden.
    return null;
  }

  return (
    <Flex justify={'space-between'} align='center' gap={2}>
      {pageCount > 1 && (
        <Flex align='center' gap={1}>
          <NavIconButton
            icon={ChevronLeftFirstIcon}
            isDisabled={isFirstPage}
            onClick={() => gotoPage(1)}
            testId='first-page'
            ariaLabel='Go to first page'
          />
          <NavIconButton
            icon={ChevronLeftIcon}
            isDisabled={isFirstPage}
            onClick={() => gotoPage(currentPage - 1)}
            testId='previous-icon'
            ariaLabel='Go to previous page'
          />
          {renderPageTemplate()}
          <NavIconButton
            icon={ChevronRightIcon}
            isDisabled={isLastPage}
            onClick={() => gotoPage(currentPage + 1)}
            testId='next-icon'
            ariaLabel='Go to next page'
          />
          <NavIconButton
            icon={ChevronRightLastIcon}
            isDisabled={isLastPage}
            onClick={() => gotoPage(pageCount)}
            testId='last-page'
            ariaLabel='Go to last page'
          />
        </Flex>
      )}

      <Box flex={1} />

      {enabledJumper && pageCount > 1 && (
        <Flex align='center' gap={2} mx={4}>
          <InnerText text={t('common.goToPage')} />
          <TextInput
            type='number'
            name='pageIndex'
            value={inputPage || ''}
            width={`${inputLength * 8 + 30}px`}
            textAlign='center'
            min={1}
            step={1}
            max={pageCount}
            onChange={handlePageInput}
            onKeyDown={(event) => {
              if (/\D/.test(event.key) && !CONTROL_KEYS.includes(event.key)) {
                event.preventDefault();
              }
            }}
            onKeyUp={(event) => {
              if (event.key === 'Enter') {
                gotoPage(inputPage);
              }
            }}
            testId='goto-page-input'
          />
          <NavIconButton
            icon={ArrowRightCircleIcon}
            onClick={() => gotoPage(inputPage)}
            testId='goto-page'
            ariaLabel='Go to page'
          />
        </Flex>
      )}
      {enabledPageSizeController && (
        <Flex align='center' gap={2}>
          <InnerText text={t('common.perPage')} />
          <Select
            data-testid='page_select'
            size='sm'
            width='4rem'
            borderRadius={4}
            value={pageSize}
            onChange={handlePageSizeChange}
          >
            {PAGE_SIZE_OPTIONS.map((pSize) => (
              <option key={pSize} value={pSize}>
                {pSize}
              </option>
            ))}
          </Select>
          <InnerText text={`${rowNumStart}-${rowNumEnd} / ${itemCount.toLocaleString()}`} />
        </Flex>
      )}
    </Flex>
  );
};

const InnerText = ({ text }: { text: string }) => (
  <Text fontSize='sm' color='black' lineHeight='1.5' whiteSpace='nowrap'>
    {text}
  </Text>
);

const NavIconButton = ({
  icon,
  testId,
  ariaLabel,
  isDisabled = false,
  onClick,
}: {
  icon: ComponentWithAs<'svg', IconProps>;
  testId: string;
  ariaLabel: string;
  isDisabled?: boolean;
  onClick: () => void;
}) => (
  <IconButton
    minWidth='unset'
    height='32px'
    colorScheme='none'
    data-testid={testId}
    aria-label={ariaLabel}
    onClick={onClick}
    isDisabled={isDisabled}
    icon={<Icon as={icon} color={isDisabled ? 'gray.300' : 'black'} boxSize='2rem' />}
  />
);

const DotsButton = () => (
  <Button
    bgColor={'unset'}
    color={'black'}
    width='2rem'
    minWidth={'2rem'}
    height='2rem'
    padding={0}
    borderRadius='lg'
    fontSize='sm'
    fontWeight='700'
    cursor={'initial'}
    _hover={{ backgroundColor: 'unset', color: 'black' }}
  >
    ...
  </Button>
);

export default TablePagination;
